/**
 * #89 · Los dos archivos de la ficha: el QR y el paquete de evidencia.
 *
 * Por qué no pasan por `useApiQuery`
 * ----------------------------------
 * `GET /labels/{code}/qr.svg` devuelve `image/svg+xml` y el paquete se baja
 * como archivo adjunto. `ApiClient.parse` hace `response.json()`, así que el
 * cliente tipado no puede traer ninguno de los dos — es la misma situación
 * que `routes/analytics/download.ts`, y se resuelve igual y por los mismos
 * motivos, incluido el de no ir a agregarle un verbo a `client.ts`, que no es
 * un archivo de este módulo.
 *
 * Lo que sí se respeta es el token. Un `<img src="/api/v1/labels/…/qr.svg">`
 * no manda el `Authorization`, y poner el token en la URL lo dejaría en el
 * historial del navegador y en el log de acceso del proxy — que es
 * exactamente donde un token no puede terminar. Por eso el QR se trae con
 * `fetch` autenticado y se rinde desde un blob.
 *
 * La regla del andamio ("nada de `useEffect` + `fetch`") sigue en pie: es
 * sobre **estado de servidor**. El QR sí lo es, así que se envuelve en
 * TanStack Query (`useQuery` con este `queryFn`) y conserva cache,
 * deduplicación y reintentos. La descarga no lo es: es un efecto disparado
 * por un click que produce un blob y termina.
 */
import type { ApiClient } from '@/api/client';
import { ApiError, asApiError } from '@/api/errors';

import { filenameFromHeader } from '../analytics/download';

export interface AssetDeps {
  api: ApiClient;
  fetchImpl?: typeof fetch;
  /** Sólo para tests: el `document` sobre el que se dispara el click. */
  doc?: Document;
}

/** El path del QR. Se arma acá para no repetir la cadena en dos vistas. */
export function qrPath(code: string, download = false): string {
  const suffix = download ? '?download=true' : '';
  return `/api/v1/labels/${encodeURIComponent(code)}/qr.svg${suffix}`;
}

export function evidencePath(code: string): string {
  return `/api/v1/labels/${encodeURIComponent(code)}/evidence`;
}

async function authenticatedFetch(
  path: string,
  deps: AssetDeps,
  accept: string,
): Promise<Response> {
  const { api, fetchImpl = api.fetcher } = deps;
  // Le pide al cliente un token utilizable: si el de la pestaña está por
  // vencer, esta llamada lo refresca. Sin esto, una pestaña abierta hace una
  // hora bajaría el archivo con un token muerto sobre un botón que "antes
  // andaba".
  await api.get<unknown>('/api/v1/auth/me');
  const session = api.currentSession();
  if (session === null) {
    throw new ApiError({ status: 401, code: 'no_session', message: 'No hay sesión iniciada.' });
  }

  let response: Response;
  try {
    // `api.resolve()` y no el path pelado: sin el prefijo del ambiente esto
    // le pega a `/api/`, que es verify y no el panel. Ver `client.ts`.
    response = await fetchImpl(api.resolve(path), {
      headers: { Authorization: `Bearer ${session.accessToken}`, Accept: accept },
      credentials: 'omit',
      redirect: 'error',
    });
  } catch (cause) {
    throw asApiError(cause);
  }
  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      code: response.status === 404 ? 'not_found' : 'label_asset_failed',
      message: 'No se pudo traer el archivo de la etiqueta.',
      traceId: response.headers.get('X-Trace-Id'),
    });
  }
  return response;
}

/**
 * El SVG del QR, como texto. Se rinde con `<img src={data URL}>`.
 *
 * Se devuelve el texto y no un object URL para que quien lo consuma no tenga
 * que acordarse de `revokeObjectURL`: un object URL vive hasta que se cierre
 * la pestaña, y una sesión de investigación abre decenas de fichas.
 */
export async function fetchLabelQr(code: string, deps: AssetDeps): Promise<string> {
  const response = await authenticatedFetch(qrPath(code), deps, 'image/svg+xml');
  return response.text();
}

/** El SVG como data URL, listo para el `src` de un `<img>`. */
export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

async function saveBlob(response: Response, fallback: string, doc: Document): Promise<string> {
  const name = filenameFromHeader(response.headers.get('Content-Disposition')) ?? fallback;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = doc.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.rel = 'noopener';
    doc.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return name;
}

/** Baja el QR como archivo. Devuelve el nombre usado. */
export async function downloadLabelQr(code: string, deps: AssetDeps): Promise<string> {
  const response = await authenticatedFetch(qrPath(code, true), deps, 'image/svg+xml');
  return saveBlob(response, `xyvona-${code}.svg`, deps.doc ?? document);
}

/**
 * Baja el paquete de evidencia. Devuelve el nombre usado.
 *
 * Cada descarga deja su entrada en `audit_log` (`label.evidence_exported`):
 * el backend lo hace solo y acá simplemente no se lo elude bajando el archivo
 * por otra vía.
 */
export async function downloadEvidence(code: string, deps: AssetDeps): Promise<string> {
  const response = await authenticatedFetch(evidencePath(code), deps, 'application/json');
  return saveBlob(response, `xyvona-evidencia-${code}.json`, deps.doc ?? document);
}
